import { genUiI18n } from '../../../shared/ui/i18n.js';
import type { FlightPlannerCopy } from '@voll-intelligence/intelligence-ui-kit/flight-planner';

/** Os textos do `PlannerForm` no locale do host. Função pura, sem Context: o
 *  card do chat a chama com o locale da conversa, e as páginas do web-chat com
 *  o do app. O host memoiza por locale quando a identidade importa. */
export function flightPlannerCopy(locale: string | null | undefined): FlightPlannerCopy {
  const { t } = genUiI18n(locale);
  return {
    travelers: {
      selectAria: t('travelers.selectAria'),
      searchPlaceholder: t('travelers.searchPlaceholder'),
      empty: t('travelers.empty'),
      searchError: t('travelers.searchError'),
      loadMore: t('travelers.loadMore'),
      you: t('travelers.you'),
      travelerRole: t('travelers.travelerRole'),
      selectedGroup: t('travelers.selectedGroup'),
      minReached: t('travelers.minReached'),
      policyLabel: t('travelers.policyLabel'),
      done: t('travelers.done'),
      back: t('travelers.back'),
      maxReached: (v) => t('travelers.maxReached', v),
      count: (v) => t(v.count === 1 ? 'travelers.count_one' : 'travelers.count_other', v),
      inSearch: (v) => t(v.count === 1 ? 'travelers.inSearch_one' : 'travelers.inSearch_other', v),
    },
    calDisabledHint: t('flightPlanner.calDisabledHint'),
    stateNeedsOut: t('flightPlanner.stateNeedsOut'),
    awaitingDate: t('flightPlanner.awaitingDate'),
    who: t('flightPlanner.who'),
    from: t('flightPlanner.from'),
    to: t('flightPlanner.to'),
    when: t('flightPlanner.when'),
    whenEmpty: t('flightPlanner.whenEmpty'),
    whenReturnPending: t('flightPlanner.whenReturnPending'),
    confirmDates: t('flightPlanner.confirmDates'),
    confirmAndSearch: t('flightPlanner.confirmAndSearch'),
    sheetOneWay: t('flightPlanner.sheetOneWay'),
    cancelEdit: t('flightPlanner.cancelEdit'),
    back: t('flightPlanner.back'),
    applyEdit: t('flightPlanner.applyEdit'),
    loadingPrices: t('flightPlanner.loadingPrices'),
    noPriceTrend: t('flightPlanner.noPriceTrend'),
    swap: t('flightPlanner.swap'),
    reset: t('flightPlanner.reset'),
    outboundTag: t('flightPlanner.outboundTag'),
    returnTag: t('flightPlanner.returnTag'),
    prevMonth: t('flightPlanner.prevMonth'),
    nextMonth: t('flightPlanner.nextMonth'),
    search: t('flightPlanner.search'),
    searchShort: t('flightPlanner.searchShort'),
    fromEmpty: t('flightPlanner.fromEmpty'),
    toEmpty: t('flightPlanner.toEmpty'),
    airportSearchPrompt: t('flightPlanner.airportSearchPrompt'),
    noAirports: t('flightPlanner.noAirports'),
    airportsSearchFailed: t('flightPlanner.airportsSearchFailed'),
    airportsSearchRetry: t('flightPlanner.airportsSearchRetry'),
    sameAirports: t('flightPlanner.sameAirports'),
    airportRecent: t('flightPlanner.airportRecent'),
    tripTypeOneWay: t('flightPlanner.tripType.oneWay'),
    tripTypeRoundTrip: t('flightPlanner.tripType.roundTrip'),
    tripTypeMultiLeg: t('flightPlanner.tripType.multiLeg'),
    multiLegEditLegs: t('flightPlanner.multiLeg.editLegs'),
    multiLegAddLeg: t('flightPlanner.multiLeg.addLeg'),
    multiLegRemoveLeg: t('flightPlanner.multiLeg.removeLeg'),
    multiLegReset: t('flightPlanner.multiLeg.reset'),
    multiLegTransfer: t('flightPlanner.multiLeg.transfer'),
    multiLegSummaryUndefined: t('flightPlanner.multiLeg.summaryUndefined'),
    stateNeedsRet: (v) => t('flightPlanner.stateNeedsRet', v),
    stateOneWay: (v) => t('flightPlanner.stateOneWay', v),
    stateRange: (v) =>
      t(v.nights === 1 ? 'flightPlanner.stateRange_one' : 'flightPlanner.stateRange_other', v),
    bestDayOut: (v) => t('flightPlanner.bestDayOut', v),
    bestDayRet: (v) => t('flightPlanner.bestDayRet', v),
    bestDayLeg: (v) => t('flightPlanner.bestDayLeg', v),
    stateLeg: (v) => t('flightPlanner.stateLeg', v),
    stateNeedsLegDate: (v) => t('flightPlanner.stateNeedsLegDate', v),
    savesLine: (v) => t('flightPlanner.savesLine', v),
    savesLineInline: (v) => t('flightPlanner.savesLineInline', v),
    chosenCheapest: t('flightPlanner.chosenCheapest'),
    confirmRange: (v) => t('flightPlanner.confirmRange', v),
    sheetOneWayTo: (v) => t('flightPlanner.sheetOneWayTo', v),
    sheetLeg: (v) => t('flightPlanner.sheetLeg', v),
    sheetLegFrom: (v) => t('flightPlanner.sheetLegFrom', v),
    analyzed: (v) => t('flightPlanner.analyzed', v),
    multiLegMissing: (v) => t(`flightPlanner.multiLeg.missing.${v.field}`, { n: v.n }),
    multiLegReady: (v) => t('flightPlanner.multiLeg.ready', v),
  };
}
